/**
 * Social Media Settings Component
 * Manages organization-level social media platform connections and settings.
 */

import React, { useState, useEffect } from 'react';
import {
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Cloud,
  Link2,
  Unlink,
  Settings,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Shield,
  Zap,
  Users,
  Target,
  Key,
  Eye,
  EyeOff,
  Save,
  Trash2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface SocialPlatform {
  id: number;
  platform_code: string;
  platform_name: string;
  platform_icon: string;
  is_active: boolean;
  supported_features: {
    post?: boolean;
    dm?: boolean;
    stories?: boolean;
  };
  rate_limits: {
    posts_per_hour?: number;
    api_calls_per_hour?: number;
  };
  is_configured: boolean;
}

interface SocialConnection {
  id: string;
  platform_code: string;
  platform_name: string;
  account_id: string;
  account_name: string;
  account_username?: string;
  account_avatar_url?: string;
  connection_status: string;
  connected_at: string;
  last_used_at?: string;
  scopes: string[];
}

interface SocialAdminSettings {
  organization_id: number;
  social_engagement_enabled: boolean;
  ml_handle_prediction_enabled: boolean;
  auto_verify_high_confidence: boolean;
  auto_verify_threshold: number;
  min_confidence_threshold: number;
  require_member_opt_in: boolean;
  platforms_enabled: string[];
  default_campaign_approval_required: boolean;
  engagement_tracking_enabled: boolean;
  daily_post_limit: number;
  monthly_post_limit: number;
}

interface PlatformCredentials {
  platform_code: string;
  platform_name: string;
  is_configured: boolean;
  has_client_id: boolean;
  has_client_secret: boolean;
  redirect_uri?: string;
  configured_at?: string;
  configured_by?: string;
}

interface SocialMediaSettingsProps {
  organizationId: number;
  isAdmin?: boolean;
}

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  bluesky: Cloud
};

const PLATFORM_COLORS: Record<string, string> = {
  facebook: 'bg-blue-600',
  instagram: 'bg-gradient-to-r from-purple-600 to-pink-500',
  twitter: 'bg-black',
  linkedin: 'bg-blue-700',
  bluesky: 'bg-sky-500'
};

export function SocialMediaSettings({ organizationId, isAdmin = false }: SocialMediaSettingsProps) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [platforms, setPlatforms] = useState<SocialPlatform[]>([]);
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [settings, setSettings] = useState<SocialAdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);

  // Admin configuration state
  const [showAdminConfig, setShowAdminConfig] = useState(false);
  const [platformCredentials, setPlatformCredentials] = useState<PlatformCredentials[]>([]);
  const [configuringPlatform, setConfiguringPlatform] = useState<string | null>(null);
  const [configForm, setConfigForm] = useState<{
    platform_code: string;
    client_id: string;
    client_secret: string;
    redirect_uri: string;
  }>({ platform_code: '', client_id: '', client_secret: '', redirect_uri: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);

  useEffect(() => {
    fetchData();
  }, [organizationId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const requests = [
        authFetch(`${getWeaveinApiUrl()}/api/social-media/platforms`),
        authFetch(`${getWeaveinApiUrl()}/api/social-media/connections?organization_id=${organizationId}`),
        authFetch(`${getWeaveinApiUrl()}/api/social-media/settings?organization_id=${organizationId}`)
      ];

      // Fetch admin credentials if admin
      if (isAdmin) {
        requests.push(
          authFetch(`${getWeaveinApiUrl()}/api/social-media/admin/platform-credentials`)
        );
      }

      const responses = await Promise.all(requests);
      const [platformsRes, connectionsRes, settingsRes, credentialsRes] = responses;

      if (platformsRes.ok) {
        setPlatforms(await platformsRes.json());
      }
      if (connectionsRes.ok) {
        setConnections(await connectionsRes.json());
      }
      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
      if (credentialsRes?.ok) {
        setPlatformCredentials(await credentialsRes.json());
      }
    } catch (err) {
      console.error('Error fetching social media data:', err);
      setError('Failed to load social media settings');
    } finally {
      setLoading(false);
    }
  };

  const saveCredentials = async () => {
    if (!configForm.platform_code || !configForm.client_id || !configForm.client_secret) {
      setError('Please fill in all required fields');
      return;
    }

    setSavingCredentials(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/admin/platform-credentials`, {
        method: 'POST',
        jsonBody: {
            platform_code: configForm.platform_code,
            client_id: configForm.client_id,
            client_secret: configForm.client_secret,
            redirect_uri: configForm.redirect_uri || undefined
          }
      });

      if (response.ok) {
        // Refresh data
        await fetchData();
        setConfiguringPlatform(null);
        setConfigForm({ platform_code: '', client_id: '', client_secret: '', redirect_uri: '' });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save credentials');
      }
    } catch (err) {
      console.error('Error saving credentials:', err);
      setError('Failed to save credentials');
    } finally {
      setSavingCredentials(false);
    }
  };

  const deleteCredentials = async (platformCode: string) => {
    if (!confirm(`Are you sure you want to remove the credentials for ${platformCode}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/admin/platform-credentials/${platformCode}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        await fetchData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to delete credentials');
      }
    } catch (err) {
      console.error('Error deleting credentials:', err);
      setError('Failed to delete credentials');
    }
  };

  const startConfiguring = (platformCode: string) => {
    setConfiguringPlatform(platformCode);
    const existing = platformCredentials.find(p => p.platform_code === platformCode);
    setConfigForm({
      platform_code: platformCode,
      client_id: '',
      client_secret: '',
      redirect_uri: existing?.redirect_uri || `https://weavein-api.onrender.com/api/social-media/callback/${platformCode}`
    });
    setShowSecret(false);
  };

  const connectPlatform = async (platformCode: string) => {
    setConnectingPlatform(platformCode);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/connect/${platformCode}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to OAuth URL
        window.location.href = data.authorization_url;
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to initiate connection');
      }
    } catch (err) {
      console.error('Error connecting platform:', err);
      setError('Failed to connect platform');
    } finally {
      setConnectingPlatform(null);
    }
  };

  const disconnectPlatform = async (connectionId: string) => {
    if (!confirm('Are you sure you want to disconnect this account?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/connections/${connectionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setConnections(connections.filter(c => c.id !== connectionId));
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to disconnect account');
      }
    } catch (err) {
      console.error('Error disconnecting platform:', err);
      setError('Failed to disconnect account');
    }
  };

  const updateSettings = async (updates: Partial<SocialAdminSettings>) => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await authFetch(`${getWeaveinApiUrl()}/api/social-media/settings?organization_id=${organizationId}`, {
        method: 'PUT',
        jsonBody: { ...settings, ...updates }
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setSettings(updatedSettings);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error updating settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getConnectionForPlatform = (platformCode: string) => {
    return connections.find(c => c.platform_code === platformCode && c.connection_status === 'active');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Loading social media settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          {!tabbed && (
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Social Media Engagement
          </h2>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Connect social platforms to boost member engagement
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Master Toggle */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                Enable Social Media Engagement
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Allow social media outreach campaigns and member engagement tracking
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={settings?.social_engagement_enabled || false}
              onChange={(e) => updateSettings({ social_engagement_enabled: e.target.checked })}
              disabled={saving}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Admin Platform Configuration */}
      {isAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowAdminConfig(!showAdminConfig)}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <div className="flex items-center">
              <Key className="w-5 h-5 mr-2 text-amber-600" />
              <h3 className="font-medium text-gray-900 dark:text-white">
                Platform API Configuration (Admin)
              </h3>
            </div>
            {showAdminConfig ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>

          {showAdminConfig && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              <div className="p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Configure OAuth credentials for each social media platform. You'll need to create apps on each platform's developer portal.
                </p>

                <div className="space-y-4">
                  {platformCredentials.map((cred) => {
                    const Icon = PLATFORM_ICONS[cred.platform_code] || Cloud;
                    const isConfiguring = configuringPlatform === cred.platform_code;

                    return (
                      <div key={cred.platform_code} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${PLATFORM_COLORS[cred.platform_code] || 'bg-gray-600'}`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {cred.platform_name}
                              </div>
                              {cred.is_configured ? (
                                <div className="flex items-center text-sm text-green-600">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Configured {cred.configured_by && `by ${cred.configured_by}`}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">
                                  Not configured
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {cred.is_configured && (
                              <button
                                onClick={() => deleteCredentials(cred.platform_code)}
                                className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                title="Remove credentials"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => isConfiguring ? setConfiguringPlatform(null) : startConfiguring(cred.platform_code)}
                              className={`px-3 py-1.5 text-sm rounded-lg ${
                                isConfiguring
                                  ? 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                            >
                              {isConfiguring ? 'Cancel' : cred.is_configured ? 'Update' : 'Configure'}
                            </button>
                          </div>
                        </div>

                        {isConfiguring && (
                          <div className="mt-4 space-y-3 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Client ID / App ID *
                              </label>
                              <input
                                type="text"
                                value={configForm.client_id}
                                onChange={(e) => setConfigForm({ ...configForm, client_id: e.target.value })}
                                placeholder={`Enter ${cred.platform_name} App ID`}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                data-form-type="other"
                                data-lpignore="true"
                                name={`oauth-client-id-${cred.platform_code}-${Date.now()}`}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Client Secret / App Secret *
                              </label>
                              <div className="relative">
                                <input
                                  type={showSecret ? 'text' : 'password'}
                                  value={configForm.client_secret}
                                  onChange={(e) => setConfigForm({ ...configForm, client_secret: e.target.value })}
                                  placeholder={`Enter ${cred.platform_name} App Secret`}
                                  autoComplete="new-password"
                                  data-form-type="other"
                                  data-lpignore="true"
                                  name={`oauth-client-secret-${cred.platform_code}-${Date.now()}`}
                                  className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => setShowSecret(!showSecret)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Redirect URI (OAuth Callback)
                              </label>
                              <input
                                type="text"
                                value={configForm.redirect_uri}
                                onChange={(e) => setConfigForm({ ...configForm, redirect_uri: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Add this URL to your app's allowed redirect URIs
                              </p>
                            </div>

                            <div className="flex justify-end pt-2">
                              <button
                                onClick={saveCredentials}
                                disabled={savingCredentials}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                              >
                                {savingCredentials ? (
                                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                  <Save className="w-4 h-4 mr-2" />
                                )}
                                Save Credentials
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    Developer Portal Links
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                      <ExternalLink className="w-3 h-3 mr-1" /> Meta (Facebook/Instagram)
                    </a>
                    <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                      <ExternalLink className="w-3 h-3 mr-1" /> Twitter/X Developer
                    </a>
                    <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                      <ExternalLink className="w-3 h-3 mr-1" /> LinkedIn Developer
                    </a>
                    <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                      <ExternalLink className="w-3 h-3 mr-1" /> Bluesky App Passwords
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Platform Connections */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
            <Link2 className="w-5 h-5 mr-2" />
            Connected Platforms
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {platforms.map((platform) => {
            const Icon = PLATFORM_ICONS[platform.platform_code] || Cloud;
            const connection = getConnectionForPlatform(platform.platform_code);
            const isEnabled = settings?.platforms_enabled?.includes(platform.platform_code);

            return (
              <div key={platform.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${PLATFORM_COLORS[platform.platform_code] || 'bg-gray-600'}`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {platform.platform_name}
                    </div>
                    {connection ? (
                      <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Connected as {connection.account_name}
                      </div>
                    ) : platform.is_configured ? (
                      <div className="text-sm text-gray-500">
                        Ready to connect
                      </div>
                    ) : (
                      <div className="text-sm text-amber-600">
                        Not configured (admin setup required)
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {/* Platform enable toggle */}
                  {connection && (
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isEnabled}
                        onChange={(e) => {
                          const newEnabled = e.target.checked
                            ? [...(settings?.platforms_enabled || []), platform.platform_code]
                            : (settings?.platforms_enabled || []).filter(p => p !== platform.platform_code);
                          updateSettings({ platforms_enabled: newEnabled });
                        }}
                        disabled={saving}
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                    </label>
                  )}

                  {/* Connect/Disconnect button */}
                  {connection ? (
                    <button
                      onClick={() => disconnectPlatform(connection.id)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 border border-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Unlink className="w-4 h-4 inline mr-1" />
                      Disconnect
                    </button>
                  ) : platform.is_configured ? (
                    <button
                      onClick={() => connectPlatform(platform.platform_code)}
                      disabled={connectingPlatform === platform.platform_code}
                      className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                    >
                      {connectingPlatform === platform.platform_code ? (
                        <RefreshCw className="w-4 h-4 inline mr-1 animate-spin" />
                      ) : (
                        <Link2 className="w-4 h-4 inline mr-1" />
                      )}
                      Connect
                    </button>
                  ) : (
                    <button
                      disabled
                      className="px-3 py-1.5 text-sm text-gray-400 border border-gray-200 rounded-lg cursor-not-allowed"
                    >
                      Not Available
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ML Prediction Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Target className="w-5 h-5 mr-2 text-purple-600" />
          <h3 className="font-medium text-gray-900 dark:text-white">
            ML Handle Prediction
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Enable ML Predictions
              </div>
              <div className="text-xs text-gray-500">
                Automatically predict social media handles from member data
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings?.ml_handle_prediction_enabled || false}
                onChange={(e) => updateSettings({ ml_handle_prediction_enabled: e.target.checked })}
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Auto-verify High Confidence
              </div>
              <div className="text-xs text-gray-500">
                Automatically verify predictions with confidence above {Math.round((settings?.auto_verify_threshold || 0.95) * 100)}%
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings?.auto_verify_high_confidence || false}
                onChange={(e) => updateSettings({ auto_verify_high_confidence: e.target.checked })}
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Minimum Confidence Threshold
            </label>
            <input
              type="range"
              min="50"
              max="95"
              value={(settings?.min_confidence_threshold || 0.75) * 100}
              onChange={(e) => updateSettings({ min_confidence_threshold: parseInt(e.target.value) / 100 })}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
              disabled={saving}
            />
            <div className="text-xs text-gray-500 mt-1">
              Show predictions with at least {Math.round((settings?.min_confidence_threshold || 0.75) * 100)}% confidence
            </div>
          </div>
        </div>
      </div>

      {/* Consent & Privacy Settings */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Shield className="w-5 h-5 mr-2 text-green-600" />
          <h3 className="font-medium text-gray-900 dark:text-white">
            Consent & Privacy
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Require Member Opt-in
              </div>
              <div className="text-xs text-gray-500">
                Only engage members who have explicitly opted in
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings?.require_member_opt_in || false}
                onChange={(e) => updateSettings({ require_member_opt_in: e.target.checked })}
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Require Campaign Approval
              </div>
              <div className="text-xs text-gray-500">
                Campaigns must be approved before sending
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings?.default_campaign_approval_required || false}
                onChange={(e) => updateSettings({ default_campaign_approval_required: e.target.checked })}
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Engagement Tracking
              </div>
              <div className="text-xs text-gray-500">
                Track likes, shares, and responses on posts
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={settings?.engagement_tracking_enabled || false}
                onChange={(e) => updateSettings({ engagement_tracking_enabled: e.target.checked })}
                disabled={saving}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-4">
          <Settings className="w-5 h-5 mr-2 text-gray-600" />
          <h3 className="font-medium text-gray-900 dark:text-white">
            Rate Limits
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Daily Post Limit
            </label>
            <input
              type="number"
              min="1"
              max="500"
              value={settings?.daily_post_limit || 50}
              onChange={(e) => updateSettings({ daily_post_limit: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={saving}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Monthly Post Limit
            </label>
            <input
              type="number"
              min="1"
              max="10000"
              value={settings?.monthly_post_limit || 500}
              onChange={(e) => updateSettings({ monthly_post_limit: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={saving}
            />
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          Saving changes...
        </div>
      )}
    </div>
  );
}

export default SocialMediaSettings;
